import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@kognitos/lattice';
import { captureException } from '@/modules/sentry';
import { errorHandler } from '@/shared/utils/error-handler';
import type { IThreadOption } from '@/shared/components';

import { buildThreadOptions } from '../utils/thread-options';
import type { IRainThreadListAdapter } from './scope';

interface IUseRainThreadActionsParams {
  /** Thread list + mutations for the surface's scope and agent. */
  adapter: IRainThreadListAdapter;
  /** Currently selected thread id (owned by the caller). */
  /** Undefined while the surface's first thread is still being created. */
  selectedThreadId: string | undefined;
  /** Bubble up a pick or a freshly-created thread id to the caller. */
  onSelectThread: (threadId: string) => void;
}

interface IUseRainThreadActionsReturn {
  /** Dropdown options, disambiguated by duplicate label. */
  options: IThreadOption[];
  /** Creates a thread, refreshes the list, and selects the new thread. */
  createNewThread: () => Promise<void>;
  /** Renames the selected thread. Rethrows so the field can revert. */
  renameSelectedThread: (nextTitle: string) => Promise<void>;
}

/**
 * Thread-list plumbing shared by every Rain thread switcher: builds the
 * option rows from the adapter's threads and wraps its create/rename
 * mutations with the toast + Sentry handling both switchers need.
 */
export function useRainThreadActions({
  adapter,
  selectedThreadId,
  onSelectThread,
}: IUseRainThreadActionsParams): IUseRainThreadActionsReturn {
  const { t } = useTranslation('rain');
  const { threads, refetch, createThread, isCreating, renameThread } = adapter;

  const options = useMemo<IThreadOption[]>(
    () => buildThreadOptions(threads, t('rain.thread.defaultTitle')),
    [threads, t],
  );

  const createNewThread = useCallback(async () => {
    if (isCreating) return;
    try {
      const nextThreadId = await createThread();
      if (!nextThreadId) return;
      // Bind the chat to the new thread immediately so the UI cannot keep
      // showing the prior thread's history if the list refetch is slow or fails
      // (QA-661). Refresh the dropdown in the background afterward.
      onSelectThread(nextThreadId);
      void refetch();
    } catch (error) {
      errorHandler(error, () => {
        toast.error(t('rain.error.createThread'));
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'create_thread' },
        });
      });
    }
  }, [createThread, isCreating, onSelectThread, refetch, t]);

  // Rethrow so EditableTitle reverts the field when the rename fails.
  const renameSelectedThread = useCallback(
    async (nextTitle: string) => {
      if (!selectedThreadId) return;
      try {
        await renameThread(selectedThreadId, nextTitle);
      } catch (error) {
        toast.error(t('rain.error.renameThread'));
        captureException(error as Error, {
          tags: { module: 'rain-chat', action: 'rename_thread' },
        });
        throw error;
      }
      // Rename already committed; refresh the list in the background. A refetch
      // failure must not toast a rename error or revert the title in
      // EditableTitle.
      void refetch();
    },
    [renameThread, refetch, selectedThreadId, t],
  );

  return { options, createNewThread, renameSelectedThread };
}
